import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DOCS_JWT_KEY, DOCS_CUSTOMER_KEY, DOCS_OIDC_LOGIN_PATH } from "../hooks/useDocsAuth";
import fullLogo from "@/assets/logos/full-logo.svg";

export function DocsLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("token");
  const sessionJwt = searchParams.get("session_jwt");
  const errorParam = searchParams.get("error");

  // Derive OIDC error message from URL param (no effect needed — pure derivation)
  const errorMessages: Record<string, string> = {
    auth_failed: "Authentication failed. Please try again.",
    invalid_request: "Invalid authentication request.",
    invalid_state: "Authentication session expired. Please try again.",
    token_exchange_failed: "Failed to complete sign-in. Please try again.",
    no_id_token: "Authentication response was incomplete. Please try again.",
    invalid_id_token: "Authentication token was invalid. Please try again.",
    no_email: "Could not determine your email address. Please contact IT.",
  };
  const initialError = errorParam
    ? (errorMessages[errorParam] ?? "An authentication error occurred.")
    : "";

  const [error, setError] = useState(initialError);
  // Initialize verifying from the URL so we render the spinner on the first
  // paint when a magic-link token or OIDC session JWT is present (but not if there's an error).
  const [verifying, setVerifying] = useState(
    !errorParam && (Boolean(magicToken) || Boolean(sessionJwt)),
  );

  // Handle OIDC callback — session JWT is passed as a query param by the
  // backend callback route. Store it in localStorage and redirect to /docs.
  useEffect(() => {
    if (!sessionJwt) return;

    localStorage.setItem(DOCS_JWT_KEY, sessionJwt);
    // Internal users don't have a customer name
    localStorage.removeItem(DOCS_CUSTOMER_KEY);
    navigate("/docs", { replace: true });
  }, [sessionJwt, navigate]);

  // Handle magic link token verification. This is the external-user access
  // path — the email-domain login form was retired in ADR-45cb51db.
  //
  // Internal session preservation: if the user already has a valid internal
  // session JWT in localStorage, send it as Authorization on the verify call.
  // The backend returns preserve_internal_session=true and we navigate to the
  // target page WITHOUT overwriting the existing session. This prevents IMs
  // from clobbering their full-access session by clicking their own
  // customer-scoped magic links (Diane's bug report 2026-05-01).
  useEffect(() => {
    if (!magicToken) return;

    let cancelled = false;

    async function verify() {
      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };

        // If an internal session JWT exists, forward it so the backend can
        // preserve it. Best-effort decode — any failure just falls through to
        // the normal external-customer path.
        const existingJwt = localStorage.getItem(DOCS_JWT_KEY);
        if (existingJwt) {
          try {
            const parts = existingJwt.split(".");
            if (parts.length === 3) {
              const payload = JSON.parse(
                atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
              );
              const notExpired =
                !payload.exp || payload.exp * 1000 > Date.now();
              if (payload.client_type === "internal" && notExpired) {
                headers["Authorization"] = `Bearer ${existingJwt}`;
              }
            }
          } catch {
            // Ignore — fall through to unauthenticated verify
          }
        }

        const res = await fetch("/api/docs/verify", {
          method: "POST",
          headers,
          body: JSON.stringify({ token: magicToken }),
        });

        if (!cancelled) {
          if (res.ok) {
            const data = await res.json();
            // Only write to localStorage when minting a new external session.
            // Preserve mode keeps the existing internal JWT untouched.
            if (!data.preserve_internal_session) {
              localStorage.setItem(DOCS_JWT_KEY, data.token);
              localStorage.setItem(DOCS_CUSTOMER_KEY, data.customer_name);
            }
            // Redirect to specific page if token carried a page_id
            const target = data.page_id ? `/docs/page/${data.page_id}` : "/docs";
            navigate(target, { replace: true });
          } else {
            const body = await res.json().catch(() => ({}));
            setError(body.detail ?? "Invalid or expired magic link");
            setVerifying(false);
          }
        }
      } catch {
        if (!cancelled) {
          setError("Failed to verify magic link. Please try again.");
          setVerifying(false);
        }
      }
    }

    verify();
    return () => {
      cancelled = true;
    };
  }, [magicToken, navigate]);

  if (verifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ev-surface-subtle">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-ev-info-text border-t-transparent" />
          <p className="text-sm text-ev-text-secondary">Verifying your access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ev-surface-subtle px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 text-center">
          <img
            src={fullLogo}
            alt="Evermore"
            className="mx-auto mb-4 h-14 w-auto"
          />
          <h1 className="text-lg font-semibold text-ev-text-primary">
            Explainers Portal
          </h1>
          <p className="mt-1 text-sm text-ev-text-secondary">
            Access is by invitation only
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-ev border border-ev-danger-border bg-ev-danger-bg px-3 py-2 text-sm text-ev-danger-text"
          >
            {error}
          </div>
        )}

        {/* Invitation-only notice */}
        <div className="rounded-ev border border-ev-border bg-ev-surface p-4 text-center text-sm text-ev-text-secondary">
          <p className="mb-2 font-medium text-ev-text-primary">
            Need access to the Explainers Portal?
          </p>
          <p>
            Your Evermore Implementation Manager can share a direct link
            scoped to your programs. If you received a link that no longer
            works, reach out to your IM for a new one.
          </p>
        </div>

        {/* Internal user sign-in */}
        <div className="mt-6 text-center">
          <a
            href={DOCS_OIDC_LOGIN_PATH}
            className="inline-flex items-center gap-2 rounded-ev px-4 py-2 text-sm font-medium text-ev-text-secondary transition-colors duration-ev hover:bg-ev-surface hover:text-ev-text-primary"
          >
            Evermore employee? Sign in with your Soda Health account
          </a>
        </div>
      </div>
    </div>
  );
}
